import type { locales } from "senf-atomic-design-system/src/components/molecules/languageSettings/LanguageSettings";
import { resolveLangForGeminiHttp } from "senf-shared/utils/geminiLangLabels";
import { parseGeminiHttpTextResponse } from "senf-shared/utils/geminiResponseParse";

export {
  getGeminiLangLabelForPrompt,
  GEMINI_LANG_LABEL_ENGLISH,
} from "senf-shared";

// senf-utopia override: Firestore/Auth/Storage live in `urban-utopia-bc48d`,
// but Cloud Functions are deployed to `senf-dev` / `senf-prod`. The shared
// `geminiRequest` derives the function URL from `firebaseConfig.projectId`,
// which would point at a project where `gemini` does not exist (CORS error
// because the OPTIONS preflight returns 404). This local copy uses the
// explicit `VITE_FIREBASE_CLOUD_FUNCTIONS_URL` env var instead, so only
// senf-utopia is affected.
const CLOUD_FUNCTIONS_BASE_URL = (() => {
  const raw = import.meta.env.VITE_FIREBASE_CLOUD_FUNCTIONS_URL;
  if (typeof raw !== "string" || !raw.trim()) {
    throw new Error(
      "[senf-utopia geminiRequest] VITE_FIREBASE_CLOUD_FUNCTIONS_URL is required.",
    );
  }
  return raw.trim().replace(/\/+$/, "");
})();

type GeminiRequestProps = {
  body: string;
  assistantId: string;
  lang?: keyof typeof locales | string;
  image?: {
    data: string;
    mimeType: string;
  };
  images?: Array<{
    data: string;
    mimeType: string;
  }>;
  signal?: AbortSignal;
  skipDlp?: boolean;
};

export const geminiRequest = async ({
  body,
  assistantId,
  lang,
  image,
  images,
  signal,
  skipDlp,
}: GeminiRequestProps) => {
  try {
    const finalImages = images || (image ? [image] : undefined);
    const functionUrl = `${CLOUD_FUNCTIONS_BASE_URL}/gemini`;

    const response = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        body: body,
        assistantId: assistantId,
        lang: resolveLangForGeminiHttp(lang),
        images: finalImages,
        ...(skipDlp ? { skipDlp } : {}),
      }),
      signal,
    });

    if (!response.ok) {
      try {
        await response.text();
      } catch (readError) {
        console.error(readError);
      }
      return null;
    }

    const contentType = response.headers.get("Content-Type");

    if (
      contentType &&
      (contentType.includes("image") || contentType.includes("octet-stream"))
    ) {
      try {
        const arrayBuffer = await response.arrayBuffer();
        if (arrayBuffer.byteLength === 0) {
          return null;
        }
        return arrayBufferToBase64(arrayBuffer);
      } catch (binaryError) {
        console.error(binaryError);
        return null;
      }
    }

    try {
      const clonedResponse = response.clone();
      const arrayBuffer = await clonedResponse.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const isProbablyBinary =
        // PNG signature
        (uint8Array[0] === 0x89 &&
          uint8Array[1] === 0x50 &&
          uint8Array[2] === 0x4e &&
          uint8Array[3] === 0x47) ||
        // JPEG signature
        (uint8Array[0] === 0xff && uint8Array[1] === 0xd8) ||
        uint8Array
          .slice(0, 100)
          .filter((b) => b < 32 && b !== 9 && b !== 10 && b !== 13).length >
          10;

      if (isProbablyBinary) {
        return arrayBufferToBase64(arrayBuffer);
      }

      const data = await response.text();
      return parseGeminiHttpTextResponse(data);
    } catch (error) {
      console.error(error);
      try {
        const textResponse = await response.text();
        return parseGeminiHttpTextResponse(textResponse);
      } catch (textError) {
        console.error(textError);
        return null;
      }
    }
  } catch (error) {
    console.error(error);
    if (error instanceof TypeError && error.message.includes("NetworkError")) {
      console.error(
        "Network error detected. This might be a CORS or connection issue.",
      );
    }
    return null;
  }
};

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
